import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Team, Formation, FormationTheme } from '../types';

import { PitchPositionItem } from './PitchPositionItem';
import { PitchPlayer } from './PitchPlayer';
import { Pitch } from './Pitch';
import { Stack } from '@mui/system';
import styled from '@emotion/styled';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SpeedDial,
  SpeedDialAction,
  Tooltip,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import TeamJerseyColor from './TeamJerseyColor';
import {
  Download,
  SettingsAccessibility,
  Person,
  ContentCopy,
  SaveAlt,
} from '@mui/icons-material';
import { toPng, toBlob } from 'html-to-image';
import { getAvailableFormations, getFormationPositions } from '../utils';
import { Bench } from './Bench';
import { shirts } from '../config/shirts';
import { CustomTeamNameDialog } from './CustomTeamNameDialog';

const SpeedDialWrapper = styled(SpeedDial)({
  '& button': {
    minWidth: 30,
    minHeight: 30,
    width: 30,
    height: 30,
  },
});

type TeamPitchFormationProps = {
  team: Team;
  onFormationChange: (team: Team, formation: Formation) => void;
  onJerseyChange: (team: Team, theme: FormationTheme) => void;
  onSetCustomTeamName?: (team: Team, customName: string) => void;
};

const exportImageFilter = (node: HTMLElement) => {
  const exclusionClasses = ['export-hidden'];
  return !exclusionClasses.some((classname) =>
    node.classList?.contains(classname)
  );
};

const TeamPitchFormation = forwardRef(
  (
    {
      team,
      onFormationChange,
      onJerseyChange,
      onSetCustomTeamName,
    }: TeamPitchFormationProps,
    ref
  ) => {
    const [formationMenuAnchorEl, setFormationMenuAnchorEl] =
      useState<null | HTMLElement>(null);
    const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] =
      useState<null | HTMLElement>(null);
    const withBenchRef = useRef(null);
    const withoutBenchRef = useRef(null);
    const [showTeamNameDialog, setShowTeamNameDialog] = useState(false);

    const pitchPositions = getFormationPositions(team.formation);

    const handleChangeFormation = (formation: Formation) => {
      onFormationChange(team, formation);
      handleFormationMenuClose();
    };

    const handleFormationMenuOpen = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      setFormationMenuAnchorEl(event.currentTarget);
    };

    const handleFormationMenuClose = () => {
      setFormationMenuAnchorEl(null);
    };

    const handleDownloadMenuOpen = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      setDownloadMenuAnchorEl(event.currentTarget);
    };

    const handleDownloadMenuClose = () => {
      setDownloadMenuAnchorEl(null);
    };

    const isBenched = team.players.some((player) => !player.position);

    const handleExportTeamImage = () => {
      handleDownloadMenuClose();

      toPng(isBenched ? withBenchRef.current! : withoutBenchRef.current!, {
        cacheBust: false,
        filter: exportImageFilter,
      })
        .then((dataUrl) => {
          const link = document.createElement('a');
          link.download = `team-${team.name}.png`;
          link.href = dataUrl;
          link.click();
        })
        .catch(alert);
    };

    const handleCopyTeamImage = () => {
      handleDownloadMenuClose();

      toBlob(isBenched ? withBenchRef.current! : withoutBenchRef.current!, {
        cacheBust: false,
        filter: exportImageFilter,
      })
        .then((blob) => {
          navigator.clipboard
            .write([
              new ClipboardItem({
                'image/png': blob as any,
              }),
            ])
            .catch((_err) => {
              alert(
                'Operation may not be supoorted in this browser. Please use Chrome'
              );
            });
        })
        .catch(alert);
    };

    const handleNameDialogSubmit = (customName: string) => {
      setShowTeamNameDialog(false);
      team.customName = customName;
      onSetCustomTeamName && onSetCustomTeamName(team, customName);
    };

    const exportImageToClipboard = async () => {
      const blob = await toBlob(
        isBenched ? withBenchRef.current! : withoutBenchRef.current!,
        {
          cacheBust: false,
          filter: exportImageFilter,
        }
      );

      return new ClipboardItem({
        'image/png': blob as any,
      });
    };

    const availableFormations: Record<number, Formation[]> =
      getAvailableFormations(team);

    useImperativeHandle(ref, () => ({
      async copyImage() {
        return await exportImageToClipboard();
      },
    }));

    return (
      <Stack
        ref={withBenchRef}
        alignItems="center"
        style={{
          width: 630,
          paddingBottom: 20,
          marginLeft: 'auto',
          marginRight: 'auto',
          backgroundColor: 'white',
        }}
      >
        <Stack
          ref={withoutBenchRef}
          style={{
            padding: '0 10px 20px 10px',
            backgroundColor: 'white',
            position: 'relative',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            style={{
              width: 600,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgb(0, 0, 0, 0.87)',
                  textTransform: 'none',
                }}
                onClick={() => setShowTeamNameDialog(true)}
              >
                <Typography
                  variant="button"
                  sx={{
                    fontSize: 18,
                    textTransform: 'none',
                    fontFamily: 'Arial',
                  }}
                >
                  {team.customName ?? `Team ${team.name}`}
                </Typography>
              </Button>
              <TeamJerseyColor team={team} />
              <Typography style={{ fontFamily: 'Arial' }}>
                {team.formation}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} className="export-hidden">
              <SpeedDialWrapper
                ariaLabel="Change jersey"
                direction="down"
                icon={<Person sx={{ fontSize: 18 }} />}
                style={{
                  position: 'absolute',
                  right: 20,
                  top: -5,
                }}
              >
                {shirts.map((shirt) => (
                  <SpeedDialAction
                    key={shirt.theme}
                    icon={
                      <img src={shirt.img} alt="shirt" style={{ width: 20 }} />
                    }
                    onClick={() => onJerseyChange(team, shirt.theme)}
                  />
                ))}
              </SpeedDialWrapper>
              <div
                style={{
                  position: 'absolute',
                  right: 70,
                  top: -10,
                }}
              >
                <Tooltip title="Download or copy image">
                  <IconButton
                    onClick={handleDownloadMenuOpen}
                    style={{
                      height: 40,
                    }}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Change formation">
                  <IconButton
                    onClick={handleFormationMenuOpen}
                    style={{
                      height: 40,
                    }}
                  >
                    <SettingsAccessibility />
                  </IconButton>
                </Tooltip>
              </div>
            </Stack>
          </Stack>
          <Pitch>
            {pitchPositions?.map((position) => (
              <PitchPositionItem
                key={position.position}
                team={team}
                position={position}
                theme={team.theme || FormationTheme.ShirtBarca}
              />
            ))}
            {team.players
              .filter((player) => !!player.position)
              .map((player) => (
                <PitchPlayer
                  key={player.name}
                  team={team}
                  player={player}
                  coords={pitchPositions?.find(
                    ({ position }) => position === player.position
                  )}
                />
              ))}
          </Pitch>
        </Stack>
        <Menu
          anchorEl={formationMenuAnchorEl}
          open={Boolean(formationMenuAnchorEl)}
          onClose={handleFormationMenuClose}
        >
          {Object.entries(availableFormations).map(
            ([playerCount, formations], index) => {
              return (
                <div key={playerCount}>
                  {formations.map((formation) => (
                    <MenuItem
                      key={formation}
                      onClick={() => handleChangeFormation(formation)}
                    >
                      <ListItemText>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'center' }}
                        >
                          <span>{formation}</span>
                          <Typography variant="caption" component="span">
                            ({playerCount}x{playerCount})
                          </Typography>
                        </Stack>
                      </ListItemText>
                    </MenuItem>
                  ))}
                  {index < Object.entries(availableFormations).length - 1 && (
                    <Divider />
                  )}
                </div>
              );
            }
          )}
        </Menu>
        <Menu
          anchorEl={downloadMenuAnchorEl}
          open={Boolean(downloadMenuAnchorEl)}
          onClose={handleDownloadMenuClose}
        >
          <MenuItem onClick={handleExportTeamImage}>
            <ListItemIcon>
              <SaveAlt />
            </ListItemIcon>
            <ListItemText>Download as image file</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleCopyTeamImage}>
            <ListItemIcon>
              <ContentCopy />
            </ListItemIcon>
            <ListItemText>Copy image to clipboard</ListItemText>
          </MenuItem>
        </Menu>
        <Bench team={team}>
          {team.players
            .filter((player) => !player.position)
            .map((player) => (
              <PitchPlayer key={player.name} team={team} player={player} />
            ))}
        </Bench>
        {showTeamNameDialog && (
          <CustomTeamNameDialog
            currentTeamName={team.customName}
            onClose={() => setShowTeamNameDialog(false)}
            onSubmit={handleNameDialogSubmit}
          />
        )}
      </Stack>
    );
  }
);

export default TeamPitchFormation;
